"use client"

import { useState, useEffect ,useRef} from "react"
import styled, { keyframes } from "styled-components"
import { User, LogOut , Camera, Loader, ArrowRight } from "lucide-react"
import NotificationPanel from "./NotificationPanel"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import { siblingOrigin, isTrustedOrigin } from '../utils/siblingSite';

export default function LoginPanel() {
  const { theme } = useTheme()
  const [isUploading, setIsUploading] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const { isLoggedIn, user, logout, login, setUser } = useAuth()
  const fileInputRef = useRef(null)
  const notificationRef = useRef(null)
  const popupRef = useRef(null)
  const popupTimerRef = useRef(null)

  // 읽지 않은 알림 개수 가져오기
  const fetchUnreadCount = async () => {
    if (!isLoggedIn) {
      setUnreadCount(0)
      return
    }
    
    try {
      const response = await fetch('/notification/unread-count', { 
        credentials: 'include' 
      })
      const data = await response.json()
      if (data.success) {
        setUnreadCount(data.count)
      }
    } catch (error) {
      console.error('읽지 않은 알림 개수 가져오기 오류:', error)
    }
  }

  // 로그인 상태 변경 시 읽지 않은 알림 개수 가져오기
  useEffect(() => {
    fetchUnreadCount()
  }, [isLoggedIn])

  // 알림 패널 외부 클릭시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false)
      }
    }

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showNotifications])

  const handleLogout = async () => {
    try {
      await logout();
      console.log("로그아웃 성공");
    } catch (error) {
      console.error("로그아웃 오류:", error);
    }
  }

  // 앤츠톡 SSO 팝업 로그인
  const handleAntsTalkPopupLogin = () => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus()
      return
    }

    const popupWidth = 480
    const popupHeight = 780
    const left = window.screenX + (window.outerWidth - popupWidth) / 2
    const top = window.screenY + (window.outerHeight - popupHeight) / 2
    // 보고 있는 계열의 앤츠톡을 연다. 주소를 박아 두면 헤츠너(.kr)에서 국내
      // 앤츠톡이 열리고, 팝업은 국내로 메시지를 보내 이 창은 아무것도 못 받는다.
      const popupUrl = `${siblingOrigin('talk')}/popup/login?parentOrigin=${encodeURIComponent(window.location.origin)}`

    const popup = window.open(
      popupUrl,
      'antsTalkLogin',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
    )

    if (!popup) {
      alert('팝업이 차단되었습니다. 브라우저의 팝업 차단을 해제해주세요.')
      return
    }

    popupRef.current = popup
    setIsLoggingIn(true)

    // 팝업 닫힘 감지 → 세션 재확인
    if (popupTimerRef.current) clearInterval(popupTimerRef.current)
    popupTimerRef.current = setInterval(async () => {
      if (popup.closed) {
        clearInterval(popupTimerRef.current)
        popupTimerRef.current = null
        popupRef.current = null
        await refreshSessionAfterLogin()
      }
    }, 500)
  }

  const refreshSessionAfterLogin = async () => {
    try {
      const res = await fetch('/auth/check-session', { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        if (data.success && data.user) {
          await login(data.user)
          setIsLoggingIn(false)
          return
        }
      }
    } catch (e) {
      console.error('SSO 세션 확인 실패:', e)
    }
    setIsLoggingIn(false)
  }

  // talk.antsnest.co.kr에서 SSO_LOGIN_SUCCESS postMessage 수신
  useEffect(() => {
    const handleMessage = async (event) => {
      // 계열을 가리지 않는다. 주소를 박으면 .kr 에서 못 받는다.
        if (!isTrustedOrigin(event.origin)) return
      if (event.data?.type !== 'SSO_LOGIN_SUCCESS') return

      // 팝업이 자체적으로 닫히지 않을 경우 대비
      if (popupRef.current && !popupRef.current.closed) {
        try { popupRef.current.close() } catch (e) {}
      }
      if (popupTimerRef.current) {
        clearInterval(popupTimerRef.current)
        popupTimerRef.current = null
      }
      popupRef.current = null
      await refreshSessionAfterLogin()
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
      if (popupTimerRef.current) clearInterval(popupTimerRef.current)
    }
  }, [])
  const handleAvatarClick = () => {
    fileInputRef.current.click();
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // 파일 유형 확인
    if (!file.type.includes('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }
    
    // 파일 크기 제한 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('파일 크기는 5MB 이하여야 합니다.');
      return;
    }
    
    setIsUploading(true);
    
    try {
      // 1. 파일 업로드
      const formData = new FormData();
      formData.append('files', file);
      
      // 파일 서버에 업로드
      const uploadResponse = await fetch('https://file.skynet.re.kr/awsUpload/send?folder=story_antsnest', {
        method: 'POST',
        body: formData,
      });
      
      const uploadData = await uploadResponse.json();
      console.log("파일 업로드 응답:", uploadData);
      
      // 응답 구조에 맞게 조건 수정
      if (uploadData.message === "Multiple files uploaded successfully" && 
          uploadData.files && 
          uploadData.files.length > 0) {
        
        const imageUrl = uploadData.files[0].url; // 첫 번째 파일의 URL 사용
        
        // 2. 사용자 테이블 업데이트
        const updateResponse = await fetch('/auth/update-avatar', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // 쿠키를 포함하여 요청
          body: JSON.stringify({
            userId: user.id,
            avatarUrl: imageUrl
          }),
        });
        
        const updateData = await updateResponse.json();
        
        if (updateData.success) {
          // 업로드 및 DB 업데이트 성공 시 사용자 정보 업데이트
          const updatedUser = {
            ...user,
            avatar_url: imageUrl
          };
          
          // 사용자 정보 업데이트
          setUser(updatedUser);
          
          console.log("프로필 이미지 업데이트 성공:", imageUrl);
        } else {
          console.error("DB 업데이트 실패:", updateData.message);
          alert('프로필 정보 업데이트에 실패했습니다.');
        }
      } else {
        console.error("이미지 업로드 실패:", uploadData);
        alert('이미지 업로드에 실패했습니다.');
      }
    } catch (error) {
      console.error("프로필 이미지 업로드 오류:", error);
      alert('프로필 이미지 업로드 중 오류가 발생했습니다.');
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <>
      <LoginCardContainer ref={notificationRef}>
      <LoginCard theme={theme}>
        <LoginHeader theme={theme}>
          <LoginTitle theme={theme}>
            {isLoggedIn ? "내 계정" : "앤츠네스트 로그인"}
          </LoginTitle>
        </LoginHeader>
        <LoginContent>
          {isLoggedIn ? (
            // 로그인된 상태: 사용자 정보와 로그아웃 버튼 표시
            <UserProfile>
              <UserInfo>
              <UserAvatarContainer onClick={handleAvatarClick}>
                  {isUploading ? (
                    <AvatarLoader>
                      <Loader size={24} className="animate-spin" />
                    </AvatarLoader>
                  ) : (
                    <>
                      {user?.avatar_url ? (
                        <UserAvatarImage src={user.avatar_url} alt="프로필 이미지" />
                      ) : (
                        <UserAvatarPlaceholder>
                          <User size={32} />
                        </UserAvatarPlaceholder>
                      )}
                      <AvatarOverlay>
                        <Camera size={16} />
                      </AvatarOverlay>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    name='files'
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </UserAvatarContainer>
                <UserDetails>
                  <UserName theme={theme}>{user?.email || "사용자"}</UserName>
                  <UserEmail theme={theme}>
                    {user?.username ? `@${user.username}` : ""}
                  </UserEmail>
                </UserDetails>
              </UserInfo>
              <LogoutButton onClick={handleLogout}>
                <LogOut size={16} />
                로그아웃
              </LogoutButton>
            </UserProfile>
          ) : (
            // 로그인되지 않은 상태: 앤츠톡 SSO 로그인 안내
            <SsoLoginWrap>
              <SsoButton theme={theme} type="button" onClick={handleAntsTalkPopupLogin} disabled={isLoggingIn}>
                {isLoggingIn ? (
                  <>
                    <Loader size={16} className="spin" />
                    로그인 처리 중...
                  </>
                ) : (
                  <>
                    앤츠톡에서 로그인
                    <ArrowRight size={16} />
                  </>
                )}
              </SsoButton>

              <SsoHelpText theme={theme}>
                앤츠톡 로그인 후 자동 로그인됩니다
              </SsoHelpText>
            </SsoLoginWrap>
          )}
        </LoginContent>
      </LoginCard>

      {/* 알림 패널 */}
      {isLoggedIn && showNotifications && (
        <NotificationDropdown theme={theme}>
          <NotificationPanel />
        </NotificationDropdown>
      )}
      </LoginCardContainer>
    </>
  )
}
// 아바타 관련 스타일
const UserAvatarContainer = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  cursor: pointer;
  overflow: hidden;
  flex-shrink: 0;
`

const UserAvatarPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b5563;
`

const UserAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const AvatarOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 4px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.2s;
  opacity: 0;
  
  ${UserAvatarContainer}:hover & {
    opacity: 1;
  }
`

const AvatarLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
`
// 로그인 상태일 때 표시할 컴포넌트 스타일
const UserProfile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`

const UserName = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: ${props => props.theme.textPrimary};
`

const UserEmail = styled.div`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
`

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #dc2626;
  }
`

const LoginCardContainer = styled.div`
  position: relative;
  
  @media (max-width: 768px) {
    z-index: 20;
    position: relative;
  }
`

const LoginCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
`

const LoginHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const LoginTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: ${props => props.theme.textPrimary};
`

const LoginContent = styled.div`
  padding: 16px;
`

const LoginButton = styled.button`
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.theme.buttonPrimaryHover};
  }
  
  &:disabled {
    background-color: ${props => props.theme.buttonSecondary};
    cursor: not-allowed;
  }
`

const LoginLinks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`

// === SSO 로그인 영역 (minimal) ===
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const SsoLoginWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const SsoButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px 16px;
  background: #2563eb;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    background: #1d4ed8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spin {
    animation: ${spin} 0.9s linear infinite;
  }
`

const SsoHelpText = styled.p`
  margin: 10px 0 0;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  text-align: center;
`

const NotificationDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 8px;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadowHover};
  max-height: 500px;
  overflow: hidden;
`